import { motion } from 'framer-motion'
import { MILESTONE_THRESHOLDS } from '@/lib/milestones'

interface XpBarProps {
  currentXp: number
  maxXp: number
  level: number
  showMilestones?: boolean
  animated?: boolean
}

export function XpBar({
  currentXp,
  maxXp,
  level,
  showMilestones = true,
  animated = true,
}: XpBarProps) {
  const percentage = Math.min((currentXp / maxXp) * 100, 100)

  // Determine which milestones have been passed
  const getMilestoneStatus = (threshold: number) => {
    const progress = currentXp / maxXp
    if (progress >= threshold) return 'passed'
    if (progress >= threshold - 0.05) return 'close'
    return 'pending'
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-secondary">Level {level}</span>
          <span className="text-xs text-text-muted">→</span>
          <span className="text-sm font-bold text-primary">Level {level + 1}</span>
        </div>
        <span className="text-sm font-semibold text-accent">
          {currentXp.toLocaleString()} / {maxXp.toLocaleString()} XP
        </span>
      </div>

      {/* Bar container */}
      <div className="relative h-5 bg-bg-dark rounded-full overflow-visible">
        {/* Progress fill */}
        <motion.div
          initial={animated ? { width: 0 } : undefined}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-secondary rounded-full animate-shimmer"
          style={{ minWidth: percentage > 0 ? '10px' : '0' }}
        />

        {/* Milestone markers */}
        {showMilestones && MILESTONE_THRESHOLDS.slice(0, -1).map((threshold) => {
          const status = getMilestoneStatus(threshold)
          const leftPos = `${threshold * 100}%`

          return (
            <div
              key={threshold}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
              style={{ left: leftPos }}
            >
              {/* Marker line */}
              <div
                className={`w-0.5 h-7 rounded-full transition-colors ${
                  status === 'passed'
                    ? 'bg-accent shadow-glow-gold'
                    : status === 'close'
                    ? 'bg-accent/50'
                    : 'bg-text-muted/30'
                }`}
              />

              {/* Milestone indicator */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-bold px-1.5 py-0.5 rounded ${
                  status === 'passed'
                    ? 'bg-accent text-bg-dark'
                    : status === 'close'
                    ? 'bg-accent/30 text-accent'
                    : 'bg-bg-card text-text-muted'
                }`}
              >
                {threshold * 100}%
              </motion.div>

              {/* Reward preview on hover */}
              {status !== 'passed' && (
                <div className="absolute top-8 left-1/2 -translate-x-1/2 opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-bg-card px-2 py-1 rounded-lg text-xs whitespace-nowrap border border-primary/20">
                    <span className="text-gem">💎</span>
                    <span className="text-accent ml-1">
                      +{threshold === 0.25 ? 5 : threshold === 0.5 ? 10 : 15}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Level up star at the end */}
        <div
          className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 z-10"
        >
          <motion.div
            animate={percentage >= 90 ? { scale: [1, 1.2, 1] } : {}}
            transition={{ repeat: Infinity, duration: 1 }}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              percentage >= 100
                ? 'bg-gradient-to-r from-accent to-yellow-400 shadow-glow-gold'
                : percentage >= 90
                ? 'bg-accent/50'
                : 'bg-bg-card border border-text-muted/30'
            }`}
          >
            <span className={`text-sm ${percentage >= 100 ? '' : 'grayscale opacity-50'}`}>
              ⭐
            </span>
          </motion.div>
        </div>
      </div>

      {/* Encouragement text */}
      <div className="mt-2 text-center">
        {percentage < 25 && (
          <span className="text-xs text-text-muted">Keep going! First milestone at 25%</span>
        )}
        {percentage >= 25 && percentage < 50 && (
          <span className="text-xs text-text-secondary">Nice! Halfway milestone coming up~</span>
        )}
        {percentage >= 50 && percentage < 75 && (
          <span className="text-xs text-accent">Over halfway! You're doing great!</span>
        )}
        {percentage >= 75 && percentage < 100 && (
          <span className="text-xs text-primary font-semibold animate-pulse">Almost there! Level up incoming~!</span>
        )}
      </div>
    </div>
  )
}
